import React, { useState, useEffect, useCallback } from 'react';
import gateApi from '../api';
import { KPICard } from '../components/UI';

// Fallback: derive today's stats from the recent transactions list when the
// /stats/live endpoint isn't available yet. (Uses the browser's local day.)
function deriveFromTransactions(transactions) {
  const sod = new Date(); sod.setHours(0, 0, 0, 0);
  const sodMs = sod.getTime();

  // NOTE: this fallback only has lifecycleStatus per transaction, not whether a
  // Challan row exists — it can't tell "needs challan" apart from "needs
  // payment" the way /stats/live can. Everything in PENDING_CHALLAN lands in
  // waitingChallanGeneration here; this only runs when the live endpoint is down.
  let waitingTareWeight = 0, waitingLoadingOrGrossWeight = 0,
      waitingChallanGeneration = 0, waitingPayment = 0, waitingExit = 0, legacyActive = 0;
  let enteredToday = 0;
  const exited = [];
  for (const t of transactions) {
    if (t.lifecycleStatus === 'PENDING_TARE_WEIGHT') waitingTareWeight++;
    else if (t.lifecycleStatus === 'PENDING_LOADING') waitingLoadingOrGrossWeight++;
    else if (t.lifecycleStatus === 'PENDING_CHALLAN') waitingChallanGeneration++;
    else if (t.lifecycleStatus === 'PENDING_EXIT') waitingExit++;
    else if (t.lifecycleStatus === 'ACTIVE') legacyActive++;
    const entry = t.entryConfirmedAt || t.entryLoggedAt;
    if (entry && new Date(entry).getTime() >= sodMs) enteredToday++;
    if (t.lifecycleStatus === 'COMPLETED' && t.exitLoggedAt &&
        new Date(t.exitLoggedAt).getTime() >= sodMs) {
      exited.push(t);
    }
  }
  const insideNow = waitingTareWeight + waitingLoadingOrGrossWeight
    + waitingChallanGeneration + waitingPayment + waitingExit + legacyActive;

  const loadMap = {};
  let totalMin = 0, n = 0;
  for (const t of exited) {
    const load = (t.freightClassification || 'UNSPECIFIED').toUpperCase();
    loadMap[load] = (loadMap[load] || 0) + 1;
    const entry = t.entryConfirmedAt || t.entryLoggedAt;
    if (entry && t.exitLoggedAt) {
      totalMin += Math.max(0, Math.round((new Date(t.exitLoggedAt) - new Date(entry)) / 60000));
      n++;
    }
  }
  const loadBreakdownToday = Object.entries(loadMap)
    .map(([load, count]) => ({ load, count }))
    .sort((a, b) => b.count - a.count);

  return {
    insideNow, enteredToday, exitedToday: exited.length,
    waitingTareWeight, waitingLoadingOrGrossWeight, waitingChallanGeneration, waitingPayment, waitingExit,
    legacyActive,
    avgMinutesInsideToday: n ? Math.round(totalMin / n) : null,
    loadBreakdownToday,
  };
}

function fmtDuration(m) {
  if (m == null) return '—';
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60), rem = m % 60;
  if (h < 24) return rem ? `${h}h ${rem}m` : `${h}h`;
  return `${Math.floor(h / 24)}d ${h % 24}h`;
}

export default function Overview({ transactions = [] }) {
  const [live, setLive] = useState(null);

  const refresh = useCallback(async () => {
    try {
      setLive(await gateApi.getLiveStats());
    } catch {
      setLive(deriveFromTransactions(transactions)); // until /stats/live is deployed
    }
  }, [transactions]);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 12000); // real-time-ish
    return () => clearInterval(id);
  }, [refresh]);

  const s = live || deriveFromTransactions(transactions);
  const loads = s.loadBreakdownToday || [];
  const maxLoad = loads.reduce((m, l) => Math.max(m, l.count), 0) || 1;
  // Only show steps that actually have a timing today — early in the morning
  // most will be empty, and a row of dashes reads as broken rather than quiet.
  const stages = (s.stageDurationsToday || []).filter(st => st.medianMinutes != null);
  const maxStage = stages.reduce((m, st) => Math.max(m, st.medianMinutes), 0) || 1;
  // The live queue, keyed so each stage tile can show how long the vehicles
  // standing in it have actually been waiting — the number the gate acts on.
  // stepKey names the step whose median that wait should be judged against, so
  // "slower than usual" is self-calibrating: a 4h wait to be loaded is normal
  // here, 4h at the payment desk is not.
  const waitingNow = Object.fromEntries((s.stageWaitingNow || []).map(w => [w.key, w]));
  const stepByKey = Object.fromEntries((s.stageDurationsToday || []).map(d => [d.key, d]));
  const sectionLabel = {
    fontSize: 10, fontWeight: 700, color: 'var(--text-muted)',
    textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 12,
  };

  return (
    <div style={{ padding: '20px 16px', display: 'flex', flexDirection: 'column', gap: 20 }}>

      {/* Live KPIs */}
      <div>
        <div style={sectionLabel}>Live · today</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <KPICard label="In Yard"        value={s.insideNow}    accent="blue"  sublabel="Vehicles inside now" />
          <KPICard label="Entered Today"  value={s.enteredToday} accent="amber" sublabel="Logged in today" />
          <KPICard label="Exited Today"   value={s.exitedToday}  accent="green" sublabel="Completed trips" />
          <KPICard label="Avg Time Inside" value={fmtDuration(s.avgMinutesInsideToday)} accent="red" sublabel="Today's exits" />
        </div>
      </div>

      {/* Yard breakdown — where every in-progress vehicle currently sits.
          Rendered as a process strip (color-coded, arrow-linked) rather than
          a flat grid so the five stages read as a sequence, not just a list
          of unrelated numbers. Colors match the STATUS_CONFIG badges used
          throughout the app (constants.js) so a number here and a badge on a
          transaction row mean the same color. */}
      <div>
        <div style={sectionLabel}>In yard · by stage</div>
        <div style={{ display: 'flex', alignItems: 'stretch', gap: 4, overflowX: 'auto', paddingBottom: 6 }}>
          {[
            { label: 'Tare',     hint: 'Just entered',      value: s.waitingTareWeight,   wait: 'tare',
              bg: '#E6F1FB', border: '#B5D4F4', color: '#0C447C' },
            { label: 'Gross',    hint: 'Out loading',        value: s.waitingLoadingOrGrossWeight, wait: 'gross',
              bg: '#FAEEDA', border: '#FAC775', color: '#633806' },
            { label: 'Challan',  hint: 'Needs billing',      value: s.waitingChallanGeneration, wait: 'challan',
              bg: '#F3E8FF', border: '#D9C2FA', color: '#5B21B6' },
            { label: 'Payment',  hint: 'Billed, unpaid',     value: s.waitingPayment,     wait: 'payment',
              bg: '#FCE4EC', border: '#F8BBD0', color: '#9D174D' },
            { label: 'Exit',     hint: 'Paid, ready',        value: s.waitingExit,        wait: 'exit',
              bg: '#FDECC8', border: '#F6D28A', color: '#7A4B06' },
            // Pre-lifecycle stragglers belong to no stage. Only rendered while any
            // remain, so this tile disappears on its own once the yard clears —
            // no follow-up cleanup needed when the cutover flag comes off.
            ...(s.legacyActive > 0 ? [{
              label: 'Legacy', hint: 'Pre-update entry', value: s.legacyActive,
              bg: '#EDEDEA', border: '#D6D6D0', color: '#4A4A46', standalone: true,
            }] : []),
          ].map((stage, i, arr) => {
            const w = stage.wait ? waitingNow[stage.wait] : null;
            const typical = w ? stepByKey[w.stepKey]?.medianMinutes : null;
            // Slower than today's own median for this step, and not a single
            // straggler — flagged so the gate can see where to send traffic.
            const slow = w?.medianMinutes != null && typical != null && w.medianMinutes > typical;
            return (
            <React.Fragment key={stage.label}>
              <div
                title={w?.oldestPlate
                  ? `${w.count} waiting · longest ${fmtDuration(w.maxMinutes)} (${w.oldestPlate})`
                  : stage.hint}
                style={{
                background: stage.bg,
                border: `1px solid ${slow ? '#B91C1C' : stage.border}`,
                borderRadius: 12, padding: '10px 14px', minWidth: 88, flexShrink: 0,
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1,
              }}>
                <div style={{ fontSize: 21, fontWeight: 700, fontFamily: 'var(--font-mono)', color: stage.color, lineHeight: 1.15 }}>
                  {stage.value ?? 0}
                </div>
                <div style={{ fontSize: 10, fontWeight: 700, color: stage.color, textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: 3 }}>
                  {stage.label}
                </div>
                {/* How long the vehicles standing here have been waiting, rather
                    than a static description — the static hint is kept only for
                    stages with nothing in them, and for the Legacy tile. */}
                <div style={{
                  fontSize: 9.5, whiteSpace: 'nowrap',
                  color: slow ? '#B91C1C' : stage.color,
                  opacity: slow ? 1 : 0.7,
                  fontWeight: slow ? 700 : 400,
                }}>
                  {w?.medianMinutes != null ? `waiting ${fmtDuration(w.medianMinutes)}` : stage.hint}
                </div>
              </div>
              {i < arr.length - 1 && (
                <div style={{
                  display: 'flex', alignItems: 'center', flexShrink: 0,
                  color: 'var(--text-muted)', opacity: 0.35, fontSize: 15,
                  // a standalone tile (Legacy) sits outside the pipeline — separate
                  // it with a divider instead of implying flow into it
                  padding: arr[i + 1].standalone ? '0 6px' : 0,
                }}>{arr[i + 1].standalone ? '│' : '→'}</div>
              )}
            </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* Time at each step — how long the step took for work FINISHED today,
          refreshed on the same 12s poll as the rest of this page. Read it with
          the strip above: this says whether today is running normally, the strip
          says where the yard is stuck right now. */}
      {stages.length > 0 && (
        <div>
          <div style={sectionLabel}>Time at each step · today</div>
          <div style={{
            background: '#fff', border: '1px solid var(--border)',
            borderRadius: 14, padding: '16px 16px 14px',
          }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 13 }}>
              {stages.map(st => (
                <div key={st.key}>
                  <div style={{
                    display: 'flex', justifyContent: 'space-between',
                    alignItems: 'baseline', gap: 10, marginBottom: 4,
                  }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>
                      {st.label}
                      {/* Each step has its own population — a step counts a trip
                          when THAT step finished, so these differ from each other.
                          Shown per row because one shared total would be wrong. */}
                      <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--text-muted)', marginLeft: 7 }}>
                        {st.trips} {st.trips === 1 ? 'trip' : 'trips'}
                      </span>
                    </span>
                    <span style={{ fontSize: 13, fontFamily: 'var(--font-mono)', color: 'var(--text-primary)' }}>
                      {fmtDuration(st.medianMinutes)}
                    </span>
                  </div>
                  <div style={{ height: 7, background: '#F1EFE8', borderRadius: 5, overflow: 'hidden' }}>
                    <div style={{
                      width: `${((st.medianMinutes ?? 0) / maxStage) * 100}%`, height: '100%',
                      background: '#0D9488', borderRadius: 5, minWidth: st.medianMinutes ? 3 : 0,
                    }} />
                  </div>
                </div>
              ))}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 12 }}>
              Median time per step, counted when the step finished today. Half of
              trips were quicker than this, half slower. Vehicles still waiting are
              not in these figures — the strip above shows those.
            </div>
          </div>
        </div>
      )}

      {/* Load carried today */}
      <div>
        <div style={sectionLabel}>Load carried · today's exits</div>
        <div style={{
          background: '#fff', border: '1px solid var(--border)',
          borderRadius: 14, overflow: 'hidden', padding: loads.length ? '14px 16px' : 0,
        }}>
          {loads.length === 0 ? (
            <div style={{ padding: '32px 20px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
              No exits recorded today yet
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {loads.map(l => (
                <div key={l.load}>
                  <div style={{
                    display: 'flex', justifyContent: 'space-between',
                    fontSize: 13, marginBottom: 5,
                  }}>
                    <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{l.load}</span>
                    <span style={{ color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
                      {l.count} {l.count === 1 ? 'trip' : 'trips'}
                    </span>
                  </div>
                  <div style={{ height: 8, background: '#F1EFE8', borderRadius: 6, overflow: 'hidden' }}>
                    <div style={{
                      width: `${(l.count / maxLoad) * 100}%`, height: '100%',
                      background: '#185FA5', borderRadius: 6,
                    }} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}